// sentry.client.config.js
// This file configures Sentry for the browser (client-side)
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";

// Auto-enable in production, or manually enable in dev via env var
const isProduction = process.env.NODE_ENV === "production";
const isEnabled = isProduction || process.env.NEXT_PUBLIC_SENTRY_ENABLED === "true";
const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;

Sentry.init({
  dsn,
  enabled: isEnabled,

  // Performance Monitoring - 100% of transactions
  tracesSampleRate: isEnabled ? 1.0 : 0,

  // Session Replay Configuration
  integrations: isEnabled ? [
    Sentry.replayIntegration({
      // Capture 100% of text content for debugging
      maskAllText: false,
      // Don't block media elements
      blockAllMedia: false,
      // Capture request/response headers and bodies for API calls in Session Replay
      networkDetailAllowUrls: [/\/api\//],
    }),
    // Canvas recording for Session Replay (captures canvas elements like image editors, charts)
    // Note: No PII scrubbing in canvas recordings
    Sentry.replayCanvasIntegration(),
  ] : [],

  // Capture 10% of all sessions for replay
  replaysSessionSampleRate: isEnabled ? 0.1 : 0,
  // Capture 10% of sessions with errors
  replaysOnErrorSampleRate: isEnabled ? 0.1 : 0,

  // Environment tagging
  environment: process.env.NODE_ENV,

  // Error Filtering - ignore non-actionable errors
  // IMPORTANT: Network errors (Failed to fetch, etc.) are NOT ignored
  // as they may indicate real API issues that need investigation
  ignoreErrors: [
    // Browser extension errors
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    /^safari-extension:\/\//,

    // ResizeObserver errors (common, non-critical)
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',

    // Promise rejection without error object
    'Non-Error promise rejection captured',

    // User-initiated cancellations ONLY (not network failures)
    'AbortError',
    'The operation was aborted',
    'The user aborted a request',

    // IndexedDB storage errors (iOS/Safari aggressively clears IndexedDB)
    // This affects Sentry Replay storage, not our app data (we use localStorage)
    // Specific pattern to avoid muting actual database errors
    /Database deleted by request of the user/,
  ],

  // Filter third-party scripts by URL pattern
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /gtag/i,
    /googletagmanager/i,
    /analytics/i,
    /facebook\.net/i,
    /hotjar/i,
  ],

  // Only capture errors from our own domains
  allowUrls: [
    /booktailor\.com/i,
    /vercel\.app/i,
    /localhost/i,
  ],

  // Filter events before sending to Sentry
  beforeSend(event, hint) {
    // Filter out browser extension errors by checking stack frames
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    const isExtensionError = frames.some(frame =>
      frame.filename?.includes('extension://') ||
      frame.filename?.includes('extensions/') ||
      frame.filename?.includes('chrome-extension://') ||
      frame.filename?.includes('moz-extension://')
    );

    if (isExtensionError) {
      return null;
    }

    // Filter out errors from third-party scripts
    const isThirdPartyError = frames.some(frame =>
      frame.filename?.includes('gtag') ||
      frame.filename?.includes('analytics') ||
      frame.filename?.includes('facebook') ||
      frame.filename?.includes('hotjar')
    );

    if (isThirdPartyError) {
      return null;
    }

    return event;
  },

  // Disable debug in production
  debug: process.env.NODE_ENV === 'development',
});
